import { ITimeProvider } from "../driven/providers/provider";

export const InsightsControllerFactory = ({
  insightsService,
  timeProvider,
}: {
  insightsService: IInsightsService;
  timeProvider: ITimeProvider;
}) => {
  const getTOCAllowedMonths: IGetTOCAllowedMonths = async ({ clinicId }) => {
    const response = await insightsService.getTOCAllowedMonthsService({
      clinicId,
    });

    return response;
  };

  const getTOPAllowedMonths: IGetTOPAllowedMonths = async ({ clinicId }) => {
    const response = await insightsService.getTOPAllowedMonthsService({
      clinicId,
    });

    return response;
  };

  const getTOC: IGetTOC = async ({ clinicId, calendar_months }) => {
    const response = await insightsService.getTOCService({
      clinicId,
      calendar_months,
    });
    if (response.success) {
      const parsedData = response.data?.sort((a, b) => {
        try {
          if (!a.PSN_ALERTS_SENT || !b.PSN_ALERTS_SENT) return 0;
          const alertsA = JSON.parse(a.PSN_ALERTS_SENT) as string[];
          const alertsB = JSON.parse(b.PSN_ALERTS_SENT) as string[];

          if (!alertsA.length || !alertsB.length) return 0;

          const firstAlertA = alertsA[0].split(": ");
          const firstAlertB = alertsB[0].split(": ");

          if (firstAlertA.length < 2 || firstAlertB.length < 2) return 0;

          return timeProvider
            .dateLibrary(firstAlertA[1])
            .isAfter(timeProvider.dateLibrary(firstAlertB[1]))
            ? -1
            : 1;
        } catch (error) {
          console.error("Error parsing PSN_ALERTS_SENT:", error);
          return 0;
        }
      });

      return { success: response.success, data: parsedData };
    }

    return response;
  };

  const getTOP: IGetTOP = async ({ clinicId, calendar_months }) => {
    const response = await insightsService.getTOPService({
      clinicId,
      calendar_months,
    });

    if (response.success) {
      const parsedData = response.data
        ?.filter((item) => item.TOP_STATUS === "True")
        .sort((a, b) => {
          return timeProvider
            .dateLibrary(a.ESCALATION_LEVEL)
            .isAfter(timeProvider.dateLibrary(b.ESCALATION_LEVEL))
            ? -1
            : 1;
        });

      return { success: response.success, data: parsedData };
    }

    return response;
  };

  const getTOCRootCause: IGetTOCRootCause = async ({ clinicId, toEventId }) => {
    const response = await insightsService.getTOCRootCauseService({
      clinicId,
      toEventId,
    });

    return response;
  };

  const getDischargeAllowedMonths: IGetDischargeAllowedMonths = async ({
    clinicId,
  }) => {
    const response = await insightsService.getDischargeAllowedMonthsService({
      clinicId,
    });

    return response;
  };

  const getDischarge: IGetDischarge = async ({ clinicId, calendar_months }) => {
    const response = await insightsService.getDischargeService({
      clinicId,
      calendar_months,
    });

    if (response.success) {
      const parsedData = response.data?.sort((a, b) => {
        return timeProvider
          .dateLibrary(a.DATE_OF_TRANSFER_LOCAL)
          .isAfter(timeProvider.dateLibrary(b.DATE_OF_TRANSFER_LOCAL))
          ? -1
          : 1;
      });

      return { success: response.success, data: parsedData };
    }

    return response;
  };

  const getOutcomesFacilities: IGetOutcomesFacilities = async ({
    clinicId,
    clinicIds,
  }) => {
    const response = await insightsService.getOutcomesFacilitiesService({
      clinicId,
      clinicIds,
    });

    return response;
  };

  const getOutcomesAvailablePeriods: IGetOutcomesAvailablePeriods = async ({
    clinicId,
    metricType,
    filterClinicId,
  }) => {
    const response = await insightsService.getOutcomesAvailablePeriodsService({
      clinicId,
      metricType,
      filterClinicId,
    });

    return response;
  };

  const getOutcomesTimeSeries: IGetOutcomesTimeSeries = async ({
    clinicId,
    metricType,
    normalization,
    patientType,
    trendType,
    startMonth,
    endMonth,
    monthsBefore,
    monthsAfter,
    clinicIds,
    regions,
    states,
    excludeClinicIds,
  }) => {
    const response = await insightsService.getOutcomesTimeSeriesService({
      clinicId,
      metricType,
      normalization,
      patientType,
      trendType,
      startMonth,
      endMonth,
      monthsBefore,
      monthsAfter,
      clinicIds,
      regions,
      states,
      excludeClinicIds,
    });

    return response;
  };

  const getOutcomesFilterOptions: IGetOutcomesFilterOptions = async ({
    clinicId,
  }) => {
    const response = await insightsService.getOutcomesFilterOptionsService({
      clinicId,
    });

    return response;
  };

  return {
    getTOC,
    getTOP,
    getTOCAllowedMonths,
    getTOPAllowedMonths,
    getTOCRootCause,
    getDischargeAllowedMonths,
    getDischarge,
    getOutcomesFacilities,
    getOutcomesAvailablePeriods,
    getOutcomesTimeSeries,
    getOutcomesFilterOptions,
  };
};
